import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import PopUpModal from '../PopUpModal/PopUpModal';
import { generateThumbnail } from '../../../Helpers/ThumbHelper';
import styles from './ReviewSelectionsModal.module.css';

// Reuse the friendly-title map used by AppliedProducts
const STEP_TITLE_MAP = {
    'Floor': 'Floor',
    'Floor_LVP': 'Floor (LVP)',
    'Floor_Hardwood': 'Floor (Hardwood)',
    'Counters': 'Countertop',
    'Counters_granite': 'Countertop (Granite)',
    'Counters_quartz': 'Countertop (Quartz)',
    'Counters_quartzite': 'Countertop (Quartzite)',
    'Counters_marble': 'Countertop (Marble)',
    'Backsplash': 'Backsplash',
    'BackspashTile': 'Backsplash Tile',
    'BacksplashTileLay': 'Backsplash Tile Lay',
    'BacksplashGrout': 'Backsplash Grout',
    'BathFloor': 'Bathroom Flooring',
    'ShowerFloor': 'Shower Floor',
    'ShowerWall': 'Shower Wall',
    'Cabinets': 'Cabinets',
    'KitchenFaucets': 'Kitchen Faucets',
    'Mirrors': 'Mirrors',
    'BathPlumbing': 'Bathroom Plumbing',
    'Paint': 'Paint',
    'Paint_Wall': 'Paint (Walls)',
    'Paint_Trim': 'Paint (Trim)',
    'KitchenArrangement': 'Kitchen Arrangement',
    'ShowerType': 'Shower Type',
    'BathType': 'Bath Type'
};

const getDisplayTitle = (stepName, selectionKey) => {
    if (stepName && STEP_TITLE_MAP[stepName]) return STEP_TITLE_MAP[stepName];
    if (selectionKey) {
        const parts = selectionKey.split('_');
        if (parts.length >= 3) {
            const stepWithSubType = `${parts[0]}_${parts[parts.length - 1]}`;
            if (STEP_TITLE_MAP[stepWithSubType]) return STEP_TITLE_MAP[stepWithSubType];
        }
        if (STEP_TITLE_MAP[parts[0]]) return STEP_TITLE_MAP[parts[0]];
    }
    const name = stepName || (selectionKey ? selectionKey.split('_')[0] : 'Unknown');
    return name.replace(/([A-Z])/g, ' $1').trim();
};

// Derive a level label from a product's grouping fields.
// Products use different fields depending on series — check the ones the
// visualizer's ProductGroupStep also groups by, in priority order.
const TIER_FIELDS = [
    'style',
    'thumbnailRenderOptions',
    'tier',
    'level',
    'ecoFriendly',
    'defaultRoomSceneSpecifier'
];
const getLevelLabel = (product) => {
    if (!product) return '';
    let raw = '';
    for (const f of TIER_FIELDS) {
        if (product[f]) { raw = String(product[f]); break; }
    }
    if (!raw) return '';
    const s = raw.trim();
    const lower = s.toLowerCase();
    // The product-catalog field uses tier words ("Standard"/"Premium") rather
    // than a numeric level. Map to Level N so the column matches the picker.
    if (lower === 'standard') return 'Level 1';
    if (lower === 'premium')  return 'Level 2';
    // Normalize shorthand like "L1"/"T1"/"level 2" to "Level N"
    const m = s.match(/^(?:l(?:evel)?|t)\s*(\d+)$/i);
    if (m) return `Level ${m[1]}`;
    return s;
};

// Consistent room ordering
const ROOM_ORDER = ['Exterior', 'Kitchen', 'Primary Bath', 'Guest Bath', 'Bathroom', 'Vanity', 'Other'];
const sortRooms = (rooms) => {
    return [...rooms].sort((a, b) => {
        const ia = ROOM_ORDER.indexOf(a);
        const ib = ROOM_ORDER.indexOf(b);
        if (ia === -1 && ib === -1) return a.localeCompare(b);
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
    });
};

export default function ReviewSelectionsModal({ isOpen, onClose, availableRooms = [] }) {
    const stepSelections = useSelector(state => state.visualizer.stepSelections);
    const activeFloorplanId = useSelector(state => state.visualizer.activeFloorplanId);
    const floorplans = useSelector(state => state.floorplan.floorplans);
    const sceneSelStrings = useSelector(state => state.visualizer.sceneSelStrings);

    const activeFloorplan = useMemo(() => {
        return (floorplans || []).find(fp => String(fp.Id || fp.id) === String(activeFloorplanId)) || null;
    }, [floorplans, activeFloorplanId]);

    const planName = activeFloorplan?.Name || activeFloorplan?.name || '';
    const planSubtitle = activeFloorplan?.['Home Type']
        || activeFloorplan?.HomeType
        || activeFloorplan?.Community
        || activeFloorplan?.community
        || '';

    // Group current selections by room
    const { groupedByRoom, sortedRooms } = useMemo(() => {
        const list = Object.entries(stepSelections || {}).map(([key, data]) => ({ key, ...data }));
        const grouped = list.reduce((acc, sel) => {
            const room = sel.room || 'Other';
            if (!acc[room]) acc[room] = [];
            acc[room].push(sel);
            return acc;
        }, {});
        return {
            groupedByRoom: grouped,
            sortedRooms: sortRooms(Object.keys(grouped))
        };
    }, [stepSelections]);

    // Room thumbnails at top — use the current sel string per room if we have it
    const roomThumbs = useMemo(() => {
        return (availableRooms || []).map(r => {
            const sel = sceneSelStrings?.[r.area] || r.sel || '-1';
            const image = `https://renderfarm2.chameleonpower.com/proxy/GetImage.ashx?spec=${encodeURIComponent(r.specifier)}&sel=${encodeURIComponent(sel)}&type=base&size=508`;
            const parts = String(r.specifier || '').split('/');
            const sceneLabel = (parts[parts.length - 1] || r.area || '').replace(/_/g, ' ');
            return { area: r.area, image, sceneLabel };
        });
    }, [availableRooms, sceneSelStrings]);

    const handlePrint = () => {
        window.open('/print.html', '_blank');
    };

    if (!isOpen) return null;

    const hasSelections = Object.keys(stepSelections || {}).length > 0;

    return (
        <PopUpModal
            handleClose={onClose}
            size="large"
            title="Review Selections"
            className={styles.modal}
            ariaLabelledBy="review-selections-title"
        >
            <div className={styles.container}>
                <h2 id="review-selections-title" className={styles.title}>Review Selections</h2>

                {roomThumbs.length > 0 && (
                    <div className={styles.thumbRow}>
                        {roomThumbs.map(({ area, image, sceneLabel }) => (
                            <div key={area} className={styles.thumbCell}>
                                <img src={image} alt={area} className={styles.thumbImage} />
                                <div className={styles.thumbCaption}>{area}{sceneLabel ? ` — ${sceneLabel}` : ''}</div>
                            </div>
                        ))}
                    </div>
                )}

                {planName && (
                    <div className={styles.planBlock}>
                        <div className={styles.planName}>{planName}</div>
                        {planSubtitle && <div className={styles.planSubtitle}>{planSubtitle}</div>}
                    </div>
                )}

                <div className={styles.tableWrap}>
                    <div className={`${styles.row} ${styles.headerRow}`}>
                        <div className={styles.colSwatch}>SWATCH</div>
                        <div className={styles.colSurface}>SURFACE</div>
                        <div className={styles.colProduct}>PRODUCT NAME</div>
                        <div className={styles.colTier}>TYPE</div>
                    </div>

                    <div className={styles.tableBody}>
                        {!hasSelections && (
                            <div className={styles.emptyState}>No products selected yet.</div>
                        )}

                        {sortedRooms.map(room => (
                            <div key={room} className={styles.roomGroup}>
                                <div className={styles.roomHeader}>{room.toUpperCase()}</div>

                                {groupedByRoom[room].map(({ key, product, stepName, tierLabel }) => {
                                    if (!product) return null;
                                    const thumb = generateThumbnail(
                                        product.id,
                                        product.specifier,
                                        '256',
                                        500,
                                        product.seriesID,
                                        product.name,
                                        product.color
                                    );
                                    return (
                                        <div key={key} className={styles.row}>
                                            <div className={styles.colSwatch}>
                                                <div className={styles.swatchBox}>
                                                    <img src={thumb} alt="" className={styles.swatchImage} />
                                                </div>
                                            </div>
                                            <div className={styles.colSurface}>
                                                {getDisplayTitle(stepName, key)}
                                            </div>
                                            <div className={styles.colProduct}>
                                                {product.color || product.name || ''}
                                            </div>
                                            <div className={styles.colTier}>
                                                {tierLabel || getLevelLabel(product)}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>

                <div className={styles.actions}>
                    <button
                        type="button"
                        className={`${styles.actionBtn} ${styles.printBtn}`}
                        onClick={handlePrint}
                    >
                        Print
                    </button>
                </div>
            </div>
        </PopUpModal>
    );
}

ReviewSelectionsModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    availableRooms: PropTypes.arrayOf(PropTypes.shape({
        area: PropTypes.string,
        specifier: PropTypes.string,
        sel: PropTypes.string,
        image: PropTypes.string
    }))
};
